import queries from '@/queries';
import FbaApiError from '@/types/FbaApiError';
import {
    VirtualRedTagEligibilityResponse,
    VirtualRedTagEvaluationResult,
    VirtualRedTagEvaluationType,
} from '@/types/VirtualRedTagEligibility';
import { defineComponent } from 'vue';
import { isNullOrUndefined } from '@/shared/helpers';

type Data = {
    virtualRedTagEligibility: VirtualRedTagEligibilityResponse | null;
    virtualRedTagEligibilityCheckFailed: boolean;
};

type EligibilityOptions = {
    trailerNumber: string;
    trailerCompany: string;
    scannedQrId?: string;
    driverCompany: string;
    driverId?: string;
};

export default defineComponent({
    data(): Data {
        return {
            virtualRedTagEligibility: null,
            virtualRedTagEligibilityCheckFailed: false,
        };
    },
    computed: {
        virtualRedTagIneligibilityReason(
            this: Data
        ): VirtualRedTagEvaluationType | null {
            const failed = this.virtualRedTagEligibility?.evaluations.find(
                (evaluation) =>
                    evaluation.result === VirtualRedTagEvaluationResult.fail
            );

            return failed?.evaluationType ?? null;
        },
        // Anything short of a clear pass means the driver has to prove they are at the
        // trailer by VIN or plate. A failed call leaves the result null, which lands
        // here as "verify" rather than waving the driver through.
        virtualRedTagRequiresIdentityVerification(this: Data): boolean {
            const proximity = this.virtualRedTagEligibility?.evaluations.find(
                (evaluation) =>
                    evaluation.evaluationType ===
                    VirtualRedTagEvaluationType.trailerProximity
            );

            return (
                isNullOrUndefined(proximity) ||
                proximity?.result !== VirtualRedTagEvaluationResult.pass
            );
        },
        // The backend is the only source that sees reservations the client never loaded,
        // so a failed call cannot be read as "not reserved". It warns instead, matching
        // how the proximity computed treats an absent answer.
        virtualRedTagReservedForAnotherDriver(this: Data): boolean {
            if (this.virtualRedTagEligibilityCheckFailed) {
                return true;
            }

            const reservation = this.virtualRedTagEligibility?.evaluations.find(
                (evaluation) =>
                    evaluation.evaluationType ===
                    VirtualRedTagEvaluationType.trailerReserved
            );

            return (
                !isNullOrUndefined(reservation) &&
                reservation?.result !== VirtualRedTagEvaluationResult.pass
            );
        },
    },
    methods: {
        async checkVirtualRedTagEligibility(
            dto: EligibilityOptions
        ): Promise<boolean> {
            const trailerIdentifier = !isNullOrUndefined(dto.scannedQrId)
                ? { scannedQrId: dto.scannedQrId }
                : {
                      trailerCompositeId: `${dto.trailerNumber}|${dto.trailerCompany}`,
                  };

            const response = await queries.getVirtualRedTagEligibility({
                ...trailerIdentifier,
                driverCompany: dto.driverCompany,
                driverId: dto.driverId,
            });

            if (response instanceof FbaApiError) {
                this.virtualRedTagEligibilityCheckFailed = true;
                return false;
            }

            this.virtualRedTagEligibilityCheckFailed = false;
            this.virtualRedTagEligibility = response;

            return response.canRedTag;
        },
    },
});
